package com.yogaaicoach.pages.workout.data

import com.yogaaicoach.pages.workout.data.models.ActionType
import com.yogaaicoach.pages.workout.data.models.MediaNarrowResponse
import com.yogaaicoach.pages.workout.data.models.MediaType

class MediaModificationService(
    private val mediaController: MediaController = DefaultMediaController()
) {

    /**
     * If this doesn't perform well in browser move to backend
     */
    fun getMapOfVideoModifications(video: MediaNarrowResponse, durationInSeconds: Int, repetitions: Int = 1): Map<Int, () -> Unit> {
        return getMapOfMediaModifications(video, durationInSeconds, MediaType.VIDEO, repetitions)
    }

    fun pauseAllMedia() {
        mediaController.pauseVideo()
        mediaController.pauseAudio()
    }

    fun resumeAllMedia() {
        mediaController.continueVideo()
        mediaController.continueAudio()
    }

    /**
     * Returns a speed action that scales [fileDuration] to fit [targetDuration], or null when no
     * speed change is needed. Used by 3-part video playback to apply a uniform speed to all parts.
     */
    fun getVideoSpeedAction(fileDuration: Int, targetDuration: Int): (() -> Unit)? {
        if (targetDuration >= fileDuration) return null
        val speed = fileDuration.toFloat() / targetDuration.toFloat()
        return { mediaController.setVideoSpeed(speed) }
    }

    /**
     * Returns a speed action that scales [loopDuration] to exactly fill [durationPerRep], or null
     * when they already match. Handles both speed-up and slow-down. Used when repetitions > 1 so
     * that one loop cycle plays for exactly one repetition's worth of time.
     */
    fun getVideoSpeedActionForDurationPerRep(loopDuration: Int, durationPerRep: Int): (() -> Unit)? {
        if (loopDuration == durationPerRep) return null
        val speed = loopDuration.toFloat() / durationPerRep.toFloat()
        return { mediaController.setVideoSpeed(speed) }
    }

    /**
     * Returns jump-to-0 actions that cycle a loop video file for the full [loopTime], always
     * playing complete cycles. The number of cycles N is rounded up so the loop file never plays
     * partially; the caller is responsible for setting the playback speed to
     * N * [loopFileDuration] / [loopTime] so all N cycles fit exactly in [loopTime].
     *
     * Jump actions fire at wall-clock seconds loopTime/N, 2*loopTime/N, …, (N-1)*loopTime/N
     * (relative to the moment the loop starts).
     *
     * Returns an empty map when [loopTime] <= [loopFileDuration] (speed-up handled upstream).
     */
    fun getLoopCycleModifications(loopFileDuration: Int, loopTime: Int): Map<Int, () -> Unit> {
        if (loopTime <= loopFileDuration) return emptyMap()
        val n = (loopTime + loopFileDuration - 1) / loopFileDuration   // ceil(loopTime / loopFileDuration)
        val map = mutableMapOf<Int, () -> Unit>()
        val cycleWallClockSeconds = loopTime.toFloat() / n.toFloat()
        for (k in 1 until n) {
            val jumpSecond = (cycleWallClockSeconds * k).toInt()
            map[jumpSecond] = { mediaController.setVideoToSecond(0) }
        }
        return map
    }

    fun getMapOfAudioModifications(audio: MediaNarrowResponse, durationInSeconds: Int, repetitions: Int = 1): Map<Int, () -> Unit> {
        return getMapOfMediaModifications(audio, durationInSeconds, MediaType.AUDIO, repetitions)
    }

    private fun getMapOfMediaModifications(media: MediaNarrowResponse, durationInSeconds: Int, mediaType: MediaType, repetitions: Int = 1): Map<Int, () -> Unit> {
        val map = mutableMapOf<Int, () -> Unit>()

        val speedMultiplier: Float
        if (durationInSeconds < media.duration && durationInSeconds > 0) {
            speedMultiplier = media.duration.toFloat() / durationInSeconds.toFloat()
            map[0] = { runSpeedAction(mediaType, speedMultiplier) }
        } else if (media.mediaAction == null && durationInSeconds > media.duration && durationInSeconds > 0) {
            speedMultiplier = media.duration.toFloat() / durationInSeconds.toFloat()
            map[0] = { runSpeedAction(mediaType, speedMultiplier) }
        } else {
            speedMultiplier = 1.0f
        }

        if (media.mediaAction != null) {
            if (media.mediaAction.action == ActionType.REPEAT) {
                val repeatActionItems = setRepeatActionTimes(
                    durationInSeconds,
                    media.duration,
                    media.mediaAction.startTime,
                    media.mediaAction.endTime,
                    mediaType,
                    speedMultiplier
                )
                map.putAll(repeatActionItems)
            } else if (media.mediaAction.action == ActionType.PAUSE) {
                val items = setPauseAction(
                    durationInSeconds,
                    media.duration,
                    media.mediaAction.startTime,
                    mediaType,
                    speedMultiplier)
                map.putAll(items)
            } else if (media.mediaAction.action == ActionType.SLOW && durationInSeconds > media.duration) {
                val items = setSlowAction(
                    durationInSeconds,
                    media.duration,
                    media.mediaAction.startTime,
                    media.mediaAction.endTime,
                    mediaType,
                    speedMultiplier
                )
                map.putAll(items)
            }
        }

        for (rep in 1 until repetitions) {
            map[rep * durationInSeconds] = { runRepeatAction(mediaType, 0) }
        }

        return map
    }

    private fun setRepeatActionTimes(
        exerciseDuration: Int,
        videoDuration: Int,
        repeatStart: Int,
        repeatEnd: Int,
        mediaType: MediaType,
        speedMultiplier: Float = 1.0f
    ): Map<Int, () -> Unit> {
        val map = mutableMapOf<Int, () -> Unit>()
        val repeatDuration = repeatEnd - repeatStart

        var timeLeftUntilRepeatEnds = exerciseDuration - videoDuration
        var currentSecond = (repeatEnd / speedMultiplier).toInt()

        while (timeLeftUntilRepeatEnds > 0) {
            val repeatTime = minOf(timeLeftUntilRepeatEnds, repeatDuration)
            val newCurrentVideoSecond = repeatEnd - repeatTime

            map[currentSecond] = { runRepeatAction(mediaType, newCurrentVideoSecond) }

            currentSecond += (repeatTime / speedMultiplier).toInt()
            timeLeftUntilRepeatEnds -= repeatTime
        }
        return map
    }

    private fun runRepeatAction(mediaType: MediaType, second: Int) {
        when (mediaType) {
            MediaType.VIDEO -> mediaController.setVideoToSecond(second)
            MediaType.AUDIO -> mediaController.setAudioToSecond(second)
        }

    }

    private fun setPauseAction(
        exerciseDuration: Int,
        mediaLength: Int,
        pauseSecond: Int,
        mediaType: MediaType,
        speedMultiplier: Float = 1.0f
    ): Map<Int, () -> Unit> {
        val map = mutableMapOf<Int, () -> Unit>()

        val pauseDuration = exerciseDuration - (mediaLength / speedMultiplier).toInt() - 1 // minus - 1 because otherwise end might be cut off

        if (pauseDuration > 0) {
            val realTimePauseSecond = (pauseSecond / speedMultiplier).toInt()
            // pause start
            map[realTimePauseSecond] = { runPauseAction(mediaType) }

            // pause end
            map[realTimePauseSecond + pauseDuration] = { runContinueAction(mediaType) }
        }

        return map
    }

    private fun runPauseAction(mediaType: MediaType) {
        when (mediaType) {
            MediaType.VIDEO -> mediaController.pauseVideo()
            MediaType.AUDIO -> mediaController.pauseAudio()
        }
    }

    private fun runContinueAction(mediaType: MediaType) {
        when (mediaType) {
            MediaType.VIDEO -> mediaController.continueVideo()
            MediaType.AUDIO -> mediaController.continueAudio()
        }
    }

    private fun setSlowAction(
        exerciseDuration: Int,
        mediaDuration: Int,
        slowStart: Int,
        slowEnd: Int,
        mediaType: MediaType,
        speedMultiplier: Float = 1.0f
    ): Map<Int, () -> Unit> {
        val map = mutableMapOf<Int, () -> Unit>()
        val gap = exerciseDuration - (mediaDuration / speedMultiplier).toInt()
        val segmentDuration = slowEnd - slowStart
        if (gap <= 0 || segmentDuration <= 0) return map

        val slowSpeed = segmentDuration.toFloat() / (segmentDuration + gap).toFloat()
        val realTimeSlowStart = (slowStart / speedMultiplier).toInt()
        val realTimeSlowEnd = realTimeSlowStart + (segmentDuration / speedMultiplier).toInt() + gap

        map[realTimeSlowStart] = { runSpeedAction(mediaType, slowSpeed) }
        map[realTimeSlowEnd] = { runSpeedAction(mediaType, 1.0f) }

        return map
    }

    private fun runSpeedAction(mediaType: MediaType, speed: Float) {
        when (mediaType) {
            MediaType.VIDEO -> mediaController.setVideoSpeed(speed)
            MediaType.AUDIO -> mediaController.setAudioSpeed(speed)
        }
    }
}